import React, {useEffect, useState} from 'react';
import {CohortRule, geneticDataOptions, phenotypicDataOptions} from "../../types/formTypes";
import TextInput from "./TextInput";
import TextAreaInput from "./TextAreaInput";
import NumberInput from "./NumberInput";
import '../componentsCSS/ClientForm.css';
import MultiSelectInput from "./MultiSelectInput";
import {SelectedView} from "../../types/types";
import {submitClientForm} from "../../api/Apis";
import CohortRulesSection from "./CohortRuleSection";
import {useNavigate} from "react-router-dom";
import {DIAGNOSIS_PATH} from "../../consts/consts";
import CheckboxInput from "./CheckboxInput";

interface ClientFormProps {
    data: Record<SelectedView, any[]>;
}

const ClientForm: React.FC<ClientFormProps> = ({data}) => {
    const navigate = useNavigate();
    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const [errorMessages, setErrorMessages] = useState<{ [key: string]: string }>({});
    const [clientName, setClientName] = useState<string>('');
    const [labManagerEmail, setLabManagerEmail] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [inclusions, setInclusions] = useState<CohortRule[]>([{type: '', value: ''}]);
    const [exclusions, setExclusions] = useState<CohortRule[]>([{type: '', value: ''}]);
    const [helsinkiFile, setHelsinkiFile] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [geneticDataAccess, setGeneticDataAccess] = useState<boolean>(false);
    const [clinicalDataAccess, setClinicalDataAccess] = useState<boolean>(false);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const base64String = reader.result?.toString().split(",")[1];
                setHelsinkiFile(base64String ?? null);
            };
            reader.onerror = (error) => console.error("Error reading file:", error);
        }
    };

    const validateCohortRules = (rules: CohortRule[]): boolean => {
        return rules.every(rule => !rule.type || rule.value);
    };

    const validateClientName = (name: string): boolean => {
        // Check if name has no spaces and uses underscores for separation
        const isValid = /^[a-zA-Z0-9_]+$/.test(name);

        if (!isValid && name) {
            setErrorMessages(prev => ({
                ...prev,
                clientName: 'Client name should not contain spaces. Use underscores for separation (e.g., client_name)'
            }));
        } else {
            setErrorMessages(prev => ({
                ...prev,
                clientName: ''
            }));
        }

        return isValid || !name;
    };

    const validateEmail = (email: string): boolean => {
        const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

        if (!isValid && email) {
            setErrorMessages(prev => ({
                ...prev,
                labManagerEmail: 'Please enter a valid email address'
            }));
        } else {
            setErrorMessages(prev => ({
                ...prev,
                labManagerEmail: ''
            }));
        }

        return isValid || !email;
    };

    const handleClientNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setClientName(value);
        validateClientName(value);
    };

    const handleLabManagerEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setLabManagerEmail(value);
        validateEmail(value);
    };

    const validate = (formData: FormData) => {
        const newErrors: { [key: string]: boolean } = {};

        const clientNameValue = formData.get('clientName') as string;
        if (!clientNameValue) {
            newErrors.clientName = true;
        } else if (!validateClientName(clientNameValue)) {
            newErrors.clientName = true;
        }

        if (!formData.get('studyObjective')) {
            newErrors.studyObjective = true;
        }

        if (!formData.get('timeDuration')) {
            newErrors.timeDuration = true;
        }

        const labManagerEmailValue = formData.get('labManagerEmail') as string;
        if (!labManagerEmailValue) {
            newErrors.labManagerEmail = true;
        } else if (!validateEmail(labManagerEmailValue)) {
            newErrors.labManagerEmail = true;
        }

        if (!formData.get('analyses')) {
            newErrors.analyses = true;
        }

        if (!formData.get('tools')) {
            newErrors.tools = true;
        }

        if (!formData.get('cohortSize')) {
            newErrors.cohortSize = true;
        }

        if (!formData.get('controlSize')) {
            newErrors.controlSize = true;
        }

        if (!formData.get('markup')) {
            newErrors.markup = true;
        }

        if (!validateCohortRules(inclusions)) {
            newErrors.inclusions = true;
        }

        if (!validateCohortRules(exclusions)) {
            newErrors.exclusions = true;
        }

        if (!geneticDataAccess && !clinicalDataAccess) {
            newErrors.dataAccess = true;
        }

        if (geneticDataAccess && (!formData.get('geneticData') || formData.get('geneticData') === '')) {
            newErrors.geneticData = true;
        }

        if (clinicalDataAccess && (!formData.get('phenotypicData') || formData.get('phenotypicData') === '')) {
            newErrors.phenotypicData = true;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const formDataToObject = (formData: FormData) => {
        const obj: any = {};

        Array.from(formData.entries()).forEach(([key, value]) => {
            if (obj[key]) {
                if (Array.isArray(obj[key])) {
                    obj[key].push(value);
                } else {
                    obj[key] = [obj[key], value];
                }
            } else {
                obj[key] = value;
            }
        });

        return obj;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);

        if (!validate(formData)) {
            return;
        }

        try {
            setIsLoading(true);
            const dataObj = formDataToObject(formData);

            const validInclusions = inclusions.filter(rule => rule.type && rule.value);
            const validExclusions = exclusions.filter(rule => rule.type && rule.value);

            dataObj.inclusions = validInclusions;
            dataObj.exclusions = validExclusions;
            dataObj.geneticDataAccess = geneticDataAccess;
            dataObj.clinicalDataAccess = clinicalDataAccess;

            if (!geneticDataAccess) {
                dataObj.geneticData = null;
            }
            if (!clinicalDataAccess) {
                dataObj.phenotypicData = null;
            }

            await submitClientForm(dataObj, helsinkiFile);
            setIsLoading(false);
            setIsModalOpen(true);
        } catch (error) {
            console.error("Form submission failed:", error);
            setIsLoading(false);
            setErrors({...errors, formSubmission: true});
        }
    };

    useEffect(() => {
        if (isModalOpen) {
            const timer = setTimeout(() => {
                navigate(DIAGNOSIS_PATH);
            }, 5000);

            return () => clearTimeout(timer);
        }
    }, [isModalOpen, navigate]);


    return (
        <>
            <form className="client-form" onSubmit={handleSubmit}>
                <div className="section">
                    <h3>General Info</h3>

                    <TextInput
                        label="Client Name"
                        subLabel="Unique name for the client (no spaces, use underscores e.g., client_name)"
                        error={errors.clientName}
                        errorMessage={errorMessages.clientName}
                        name="clientName"
                        required={true}
                        onChange={handleClientNameChange}
                        value={clientName}
                    />

                    <TextAreaInput
                        label="Study Objective"
                        name="studyObjective"
                        subLabel="Short description of the study purpose"
                        error={errors.studyObjective}
                        required={true}
                    />

                    <NumberInput
                        label="Time Duration"
                        subLabel="Time duration from when data becomes accessible to termination, in months"
                        name="timeDuration"
                        placeholder="E.g., 12"
                        error={errors.timeDuration}
                        required={true}
                    />

                    <TextInput
                        label="Research Lab Manager Email"
                        subLabel="Email address of the research lab manager"
                        name="labManagerEmail"
                        placeholder="e.g., manager@example.com"
                        error={errors.labManagerEmail}
                        errorMessage={errorMessages.labManagerEmail}
                        required={true}
                        onChange={handleLabManagerEmailChange}
                        value={labManagerEmail}
                    />

                    <NumberInput
                        label="Markup"
                        subLabel="Markup percentage (e.g., 120%)"
                        name="markup"
                        placeholder="e.g., 120"
                        error={errors.markup}
                        required={true}
                    />

                    <TextInput
                        label="AWS Account ID"
                        subLabel="AWS account ID of the client"
                        error={errors.awsAccountId}
                        name="awsAccountId"
                        required={false}
                    />

                    <div className="cohort-rule-file">
                        <label>Helsinki File (PDF)</label>
                        <input
                            className={`input-file ${errors.helsinkiFile ? 'error-border' : ''}`}
                            type="file"
                            accept="application/pdf"
                            onChange={handleFileChange}
                            required={false}
                        />
                    </div>

                </div>

                <div className="section">
                    <h3>Data to Be Accessed</h3>

                    <div className="form-field">
                        <label className={`required-field ${errors.dataAccess ? 'error-text' : ''}`}>Data Access</label>
                        <div className="checkbox-group">
                            <CheckboxInput
                                label="Genetic Data Access"
                                name="geneticDataAccess"
                                checked={geneticDataAccess}
                                onChange={(e) => setGeneticDataAccess(e.target.checked)}
                            />
                            <CheckboxInput
                                label="Clinical Data Access"
                                name="clinicalDataAccess"
                                checked={clinicalDataAccess}
                                onChange={(e) => setClinicalDataAccess(e.target.checked)}
                            />
                        </div>
                        {errors.dataAccess &&
                            <div className="error-message">At least one data access option must be selected</div>}
                    </div>

                    {geneticDataAccess && (
                        <MultiSelectInput
                            label="Genetic data to be accessed"
                            name="geneticData"
                            options={geneticDataOptions}
                            placeholder="Select genetic data types..."
                            error={errors.geneticData}
                            required={false}
                        />
                    )}
                    {!geneticDataAccess && <input type="hidden" name="geneticData" value="" />}

                    {clinicalDataAccess && (
                        <MultiSelectInput
                            label="Specific clinical data to be accessed"
                            name="phenotypicData"
                            options={phenotypicDataOptions}
                            placeholder="Select phenotypic data types..."
                            error={errors.phenotypicData}
                            required={false}
                        />
                    )}
                    {!clinicalDataAccess && <input type="hidden" name="phenotypicData" value="" />}
                </div>

                <div className="section">
                    <h3>Cohort</h3>
                    <NumberInput
                        label="Cohort Size"
                        name="cohortSize"
                        error={errors.cohortSize}
                        required={true}
                    />
                    <NumberInput
                        label="Control Size"
                        name="controlSize"
                        error={errors.controlSize}
                        required={true}
                    />
                    <CohortRulesSection label="Inclusions" rules={inclusions} setRules={setInclusions}
                                        treeData={data} error={errors.inclusions} required={false}/>
                    <CohortRulesSection label="Exclusions" rules={exclusions} setRules={setExclusions}
                                        treeData={data} error={errors.exclusions} required={false}/>
                </div>

                <div className="section">
                    <h3>Analyses</h3>
                    <TextAreaInput
                        label="Type of planned analyses to perform"
                        rows={2}
                        name="analyses"
                        placeholder="E.g., Polygenic risk score"
                        error={errors.analyses}
                        required={true}
                    />
                </div>

                <div className="section">
                    <h3>Tools</h3>
                    <TextAreaInput
                        label="Type of planned tools required to perform the analyses"
                        rows={2}
                        name="tools"
                        placeholder="E.g., Custom code in R"
                        error={errors.tools}
                        required={true}
                    />
                </div>

                <div className="submit-wrapper">
                    <button
                        type="submit"
                        className={`submit-btn ${isLoading ? 'disabled-btn' : ''}`}
                        disabled={isLoading}
                    >
                        {isLoading ? <div className="spinner"></div> : "Submit"}
                    </button>
                </div>
                {errors.formSubmission && (
                    <div className="form-error">
                        There was an error submitting the form. Please try again later.
                    </div>
                )}
            </form>
            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h2>Form Submitted Successfully!</h2>
                        <p>Redirecting back to the main page...</p>
                        <p>You will be redirected shortly.</p>
                    </div>
                </div>
            )}
        </>
    );
};

export default ClientForm;
